import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardBody, FormGroup, Label, Input, Button, Spinner } from 'reactstrap';
import { useSelector } from 'react-redux';
import { notificationService, NotificationTypes, NotificationChannels } from '../../../services/notificationService';
import { selectLanguageUse } from '../../../redux/slices/language.Slice';
import toast from 'react-hot-toast';

const NotificationPreferences = () => {
    const [preferences, setPreferences] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const languageUse = useSelector(selectLanguageUse);

    const loadPreferences = async () => {
        setLoading(true);
        try {
            const userPreferences = await notificationService.getUserPreferences();
            setPreferences(userPreferences);
        } catch (error) {
            console.error('Error loading preferences:', error);
            toast.error(languageUse === 'English' ? 'Failed to load preferences' : 'Échec du chargement des préférences');
        }
        setLoading(false);
    };

    useEffect(() => {
        loadPreferences();
    }, []);

    const handleToggle = (type, channel) => {
        setPreferences(prev => ({
            ...prev,
            [type]: {
                ...prev[type],
                [channel]: !prev[type]?.[channel]
            }
        }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            await notificationService.updateUserPreferences(user.id, preferences);
            toast.success(languageUse === 'English' ? 'Preferences saved successfully' : 'Préférences enregistrées avec succès');
        } catch (error) {
            toast.error(languageUse === 'English' ? 'Failed to save preferences' : 'Échec de l\'enregistrement des préférences');
        } finally {
            setSaving(false);
        }
    };

    const renderNotificationSection = (title, types) => (
        <div className="mb-4">
            <h5 className="mb-3">{title}</h5>
            {types.map(type => (
                <div key={type} className="mb-3">
                    <Label className="d-flex justify-content-between align-items-center">
                        <span>{type}</span>
                        <div>
                            <FormGroup check inline>
                                <Label check>
                                    <Input
                                        type="checkbox"
                                        checked={preferences[type]?.email || false}
                                        onChange={() => handleToggle(type, 'email')}
                                    />
                                    {languageUse === 'English' ? 'Email' : 'E-mail'}
                                </Label>
                            </FormGroup>
                            <FormGroup check inline>
                                <Label check>
                                    <Input
                                        type="checkbox"
                                        checked={preferences[type]?.inApp || false}
                                        onChange={() => handleToggle(type, 'inApp')}
                                    />
                                    {languageUse === 'English' ? 'In-App' : 'Dans l\'application'}
                                </Label>
                            </FormGroup>
                        </div>
                    </Label>
                </div>
            ))}
        </div>
    );

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                <Spinner />
            </div>
        );
    }

    return (
        <Card>
            <CardHeader>
                <h4>{languageUse === 'English' ? 'Notification Preferences' : 'Préférences de notification'}</h4>
            </CardHeader>
            <CardBody>
                {renderNotificationSection(
                    languageUse === 'English' ? 'Account Notifications' : 'Notifications de compte',
                    [
                        NotificationTypes.ACCOUNT_CREATED,
                        NotificationTypes.PASSWORD_CHANGED,
                        NotificationTypes.PROFILE_UPDATED,
                        NotificationTypes.SECURITY_ALERT,
                        NotificationTypes.EMAIL_VERIFICATION
                    ]
                )}

                {renderNotificationSection(
                    languageUse === 'English' ? 'Assessment Notifications' : 'Notifications d\'évaluation',
                    [
                        NotificationTypes.SUBMISSION_DEADLINE,
                        NotificationTypes.SUBMISSION_COMPLETED,
                        NotificationTypes.ASSESSMENT_PROGRESS,
                        NotificationTypes.MISSING_INFO,
                        NotificationTypes.SUBMISSION_STATUS
                    ]
                )}

                {renderNotificationSection(
                    languageUse === 'English' ? 'System Notifications' : 'Notifications système',
                    [
                        NotificationTypes.SYSTEM_MAINTENANCE,
                        NotificationTypes.SERVICE_UPDATE,
                        NotificationTypes.TECHNICAL_ISSUE,
                        NotificationTypes.NEW_FEATURE,
                        NotificationTypes.PERFORMANCE_ALERT
                    ]
                )}

                {renderNotificationSection(
                    languageUse === 'English' ? 'Report Notifications' : 'Notifications de rapport',
                    [
                        NotificationTypes.REPORT_GENERATED,
                        NotificationTypes.REPORT_REVIEW,
                        NotificationTypes.REPORT_APPROVED,
                        NotificationTypes.REPORT_SHARED,
                        NotificationTypes.REPORT_UPDATED
                    ]
                )}

                {renderNotificationSection(
                    languageUse === 'English' ? 'Compliance Notifications' : 'Notifications de conformité',
                    [
                        NotificationTypes.POLICY_UPDATE,
                        NotificationTypes.COMPLIANCE_DEADLINE,
                        NotificationTypes.DATA_SUBMISSION,
                        NotificationTypes.REGULATORY_UPDATE,
                        NotificationTypes.PRIVACY_UPDATE
                    ]
                )}

                {renderNotificationSection(
                    languageUse === 'English' ? 'Support Notifications' : 'Notifications de support',
                    [
                        NotificationTypes.SUPPORT_TICKET,
                        NotificationTypes.QUERY_RESPONSE,
                        NotificationTypes.FEEDBACK_REQUEST,
                        NotificationTypes.SURVEY_INVITATION,
                        NotificationTypes.NEWSLETTER
                    ]
                )}

                <div className="mt-4">
                    <Button
                        color="primary"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? (
                            <Spinner size="sm" />
                        ) : (
                            languageUse === 'English' ? 'Save Preferences' : 'Enregistrer les préférences'
                        )}
                    </Button>
                </div>
            </CardBody>
        </Card>
    );
};

export default NotificationPreferences; 